import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import './notifications.css';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import {
  Form,
  FormField,
  FormItem,
  FormControlWithFocus,
  useFormValidation,
} from '../../../../shared/components/form';
import {
  fetchCreator,
  updateCreator,
} from '../../../../shared/actions/creatorActions';
import { createNotificationPreferencesSchema } from '../../../../shared/validations/setting';
import { ROLE } from '../../../../shared/constants/common';
import { ROUTES } from '../../../../shared/constants/routes';
import {
  getUserRole,
  isUserContentCreator,
  isUserLoggedIn,
} from '../../../../shared/utils/function';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { getLocalItem } from '../../../../shared/utils/config/localStorage';
import apiCache from '../../../../shared/utils/apiCache';
import * as SwalHandled from '../../../../shared/utils/swalHandler';
import { showManualSuccessPushToast } from '../../../../shared/pusNotification/useManualToasts';

// Constants
const NOTIFICATION_FIELDS = {
  MASTER: 'notifications.master',
  SUBSCRIBERS: 'notifications.subscribers',
  EARNINGS: 'notifications.earnings',
  PAYOUTS: 'notifications.payouts',
  MESSAGES: 'notifications.messages',
};

const NOTIFICATION_KEYS = [
  'master',
  'messages',
  'subscribers',
  'earnings',
  'payouts',
];

const ModernCreatorNotifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const isLoggedIn = isUserLoggedIn();
  const isCreator = isUserContentCreator();
  const userRole = useMemo(() => getUserRole(), []);
  const shouldRedirectToUserNotifications = useMemo(() => {
    if (userRole === null || userRole === undefined) {
      return false;
    }

    if (typeof userRole === 'string') {
      const normalizedRole = userRole.trim().toLowerCase();
      return (
        normalizedRole === 'user' ||
        normalizedRole === String(ROLE.USER).toLowerCase()
      );
    }

    return Number(userRole) === ROLE.USER;
  }, [userRole]);

  const { creator: creatorProfile } = useSelector(
    (state) => state.creator || {}
  );

  const schema = useMemo(() => createNotificationPreferencesSchema(), []);
  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const defaultValues = useMemo(
    () => ({
      notifications: {
        master: true,
        messages: true,
        subscribers: true,
        earnings: true,
        payouts: true,
      },
    }),
    []
  );

  const form = useForm({
    resolver,
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { validateAndFocus } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  const [initialValues, setInitialValues] = useState(defaultValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const masterValue = Boolean(form.watch(NOTIFICATION_FIELDS.MASTER));
  const subscriberValue = Boolean(form.watch(NOTIFICATION_FIELDS.SUBSCRIBERS));
  const earningValue = Boolean(form.watch(NOTIFICATION_FIELDS.EARNINGS));
  const payoutValue = Boolean(form.watch(NOTIFICATION_FIELDS.PAYOUTS));
  const messageValue = Boolean(form.watch(NOTIFICATION_FIELDS.MESSAGES));

  const registeredEmail = useMemo(
    () => creatorProfile?.user_data?.email || '',
    [creatorProfile]
  );

  const creatorUsername = useMemo(
    () => creatorProfile?.user_data?.username || null,
    [creatorProfile]
  );

  const isPPVAccount = creatorProfile?.user_data?.rol === ROLE.CREATOR_CONTENT;

  const hasChanges = useMemo(() => {
    const current = {
      master: masterValue,
      subscribers: subscriberValue,
      earnings: earningValue,
      payouts: payoutValue,
      messages: messageValue,
    };

    const previous = initialValues.notifications || {};

    return (
      current.master !== Boolean(previous.master) ||
      current.subscribers !== Boolean(previous.subscribers) ||
      current.earnings !== Boolean(previous.earnings) ||
      current.payouts !== Boolean(previous.payouts) ||
      current.messages !== Boolean(previous.messages)
    );
  }, [
    earningValue,
    initialValues.notifications,
    masterValue,
    messageValue,
    payoutValue,
    subscriberValue,
  ]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate.toSignup();
      return;
    }

    if (shouldRedirectToUserNotifications) {
      navigate.to(ROUTES.NOTIFICATIONS, { replace: true });
      return;
    }

    if (!isCreator) {
      navigate.toDiscover();
    }
  }, [isCreator, isLoggedIn, navigate, shouldRedirectToUserNotifications]);

  useEffect(() => {
    if (!isLoggedIn || !isCreator) {
      return;
    }

    const storedUsername = getLocalItem('username', '');

    if (storedUsername) {
      dispatch(fetchCreator(storedUsername));
    }
  }, [dispatch, isCreator, isLoggedIn]);

  useEffect(() => {
    if (!creatorProfile?.user_data) {
      return;
    }

    const notificationsSource = creatorProfile.user_data.notifications || {};

    const nextValues = {
      notifications: {
        master: notificationsSource.master !== false,
        messages: notificationsSource.messages !== false,
        subscribers: notificationsSource.subscribers !== false,
        earnings: notificationsSource.earnings !== false,
        payouts: notificationsSource.payouts !== false,
      },
    };

    setInitialValues((prev) => {
      const previous = prev.notifications || {};
      const updated = nextValues.notifications;

      const hasPreferenceChanged = NOTIFICATION_KEYS.some(
        (key) => previous[key] !== updated[key]
      );

      if (hasPreferenceChanged) {
        form.reset(nextValues, { keepDirty: false });
        return nextValues;
      }

      return prev;
    });
  }, [creatorProfile, form]);

  const handleSavePreferences = useCallback(
    async (values) => {
      const transformedPreferences = {
        master: Boolean(values.notifications.master),
        messages: Boolean(values.notifications.messages),
        subscribers: Boolean(values.notifications.subscribers),
        earnings: Boolean(values.notifications.earnings),
        payouts: Boolean(values.notifications.payouts),
      };

      setIsSubmitting(true);

      try {
        const result = await updateCreator({
          notifications: transformedPreferences,
        });

        if (!result?.success) {
          throw new Error(
            result?.error ||
              'Failed to save notification preferences. Please try again.'
          );
        }

        apiCache.clearAccountInformationCache();

        const usernameToRefresh = creatorUsername || getLocalItem('username');
        if (usernameToRefresh) {
          dispatch(fetchCreator(usernameToRefresh));
        }

        setInitialValues({ notifications: transformedPreferences });
        form.reset(
          { notifications: transformedPreferences },
          { keepDirty: false }
        );

        showManualSuccessPushToast(
          'Notifications Updated',
          'Your notification preferences have been saved successfully.',
          creatorProfile?.user_data || creatorProfile
        );
      } catch (error) {
        await SwalHandled.showError(
          'Error',
          error?.response?.data?.message ||
            error?.message ||
            'Failed to save notification preferences. Please try again.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [creatorUsername, dispatch, form]
  );

  const handleMasterToggle = useCallback(
    (nextValue, form) => {
      const currentSubscribers = Boolean(
        form.getValues(NOTIFICATION_FIELDS.SUBSCRIBERS)
      );
      const currentEarnings = Boolean(
        form.getValues(NOTIFICATION_FIELDS.EARNINGS)
      );
      const currentPayouts = Boolean(
        form.getValues(NOTIFICATION_FIELDS.PAYOUTS)
      );
      const currentMessages = Boolean(
        form.getValues(NOTIFICATION_FIELDS.MESSAGES)
      );

      const allFalse =
        !currentSubscribers &&
        !currentEarnings &&
        !currentPayouts &&
        !currentMessages;

      const allTrue =
        currentSubscribers &&
        currentEarnings &&
        currentPayouts &&
        currentMessages;

      const valueToSet = allFalse ? true : allTrue ? false : nextValue;

      const notificationFields = [
        NOTIFICATION_FIELDS.MASTER,
        NOTIFICATION_FIELDS.SUBSCRIBERS,
        NOTIFICATION_FIELDS.EARNINGS,
        NOTIFICATION_FIELDS.PAYOUTS,
        NOTIFICATION_FIELDS.MESSAGES,
      ];

      notificationFields.forEach((fieldName) => {
        form.setValue(fieldName, valueToSet, {
          shouldDirty: true,
        });
      });
    },
    []
  );

  const handleIndividualToggle = useCallback(
    (name, nextValue, form) => {
      const currentValues = form.getValues('notifications');

      const updatedSubscribers =
        name === NOTIFICATION_FIELDS.SUBSCRIBERS
          ? nextValue
          : Boolean(currentValues.subscribers);
      const updatedEarnings =
        name === NOTIFICATION_FIELDS.EARNINGS
          ? nextValue
          : Boolean(currentValues.earnings);
      const updatedPayouts =
        name === NOTIFICATION_FIELDS.PAYOUTS
          ? nextValue
          : Boolean(currentValues.payouts);
      const updatedMessages =
        name === NOTIFICATION_FIELDS.MESSAGES
          ? nextValue
          : Boolean(currentValues.messages);

      form.setValue(name, nextValue, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });

      const allTrueAfterToggle =
        updatedSubscribers &&
        updatedEarnings &&
        updatedPayouts &&
        updatedMessages;

      form.setValue(
        NOTIFICATION_FIELDS.MASTER,
        allTrueAfterToggle,
        {
          shouldDirty: true,
        }
      );
    },
    []
  );

  const handleToggleChange = useCallback(
    (name, nextValue, isMaster, field) => {
      if (isMaster) {
        handleMasterToggle(nextValue, form);
      } else {
        handleIndividualToggle(name, nextValue, form);
        field.onChange(nextValue);
      }
    },
    [form, handleMasterToggle, handleIndividualToggle]
  );

  const onSubmit = useCallback(
    async (values) => {
      const currentFormValues = form.getValues();
      const notificationsData =
        currentFormValues.notifications || values.notifications;

      const isValid = await validateAndFocus();

      if (!isValid) {
        return;
      }

      if (!hasChanges) {
        await SwalHandled.showInfo('No Changes', 'Nothing to update.');
        return;
      }

      const valuesToSubmit = {
        ...values,
        notifications: notificationsData,
      };

      await handleSavePreferences(valuesToSubmit);
    },
    [form, handleSavePreferences, hasChanges, validateAndFocus]
  );

  const toggleConfig = useMemo(
    () => [
      {
        key: 'master',
        name: NOTIFICATION_FIELDS.MASTER,
        label: 'All Notifications',
        description: isPPVAccount
          ? 'Get notified on new followers, earnings, payouts, call requests, and priority messages'
          : 'Get notified on new subscribers, earnings, payouts, call requests, and priority messages',
        isMaster: true,
      },
      {
        key: 'subscribers',
        name: NOTIFICATION_FIELDS.SUBSCRIBERS,
        label: isPPVAccount ? 'New Followers' : 'New Subscribers',
        description: isPPVAccount
          ? 'Get notified when someone follows your account'
          : 'Get notified when someone subscribes to your account',
      },
      {
        key: 'earnings',
        name: NOTIFICATION_FIELDS.EARNINGS,
        label: 'Earning Notifications',
        description: 'Get notified when you receive tips and purchases',
      },
      {
        key: 'payouts',
        name: NOTIFICATION_FIELDS.PAYOUTS,
        label: 'Payout Notifications',
        description: 'Get notified when you initiate a payout',
      },
      {
        key: 'messages',
        name: NOTIFICATION_FIELDS.MESSAGES,
        label: 'Message Notifications',
        description: 'Get notified when you receive a priority message and call requests',
      },
    ],
    [isPPVAccount]
  );

  if (!isLoggedIn || !isCreator) {
    return null;
  }

  const isSaveDisabled =
    isSubmitting || form.formState.isSubmitting || !hasChanges;

  return (
    <div className="modern-creator-notifications-page">
      <ModernHeader
        customTitle="Notifications"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
      />

      <main className="modern-creator-notifications-main mt-4">
        <section className="modern-creator-notifications-email-section">
          <div className="modern-creator-notifications-email-card">
            <h2 className="modern-creator-notifications-email-title">
              Email Notifications
            </h2>
            <div className="modern-creator-notifications-email-info">
              <div className="modern-creator-notifications-email-details">
                <p className="modern-creator-notifications-email-label">
                  Registered Email
                </p>
                <p className="modern-creator-notifications-email-value">
                  {registeredEmail || 'Not available'}
                </p>
              </div>
              <div className="modern-creator-notifications-email-badge">
                Saved
              </div>
            </div>
            <p className="modern-creator-notifications-email-note">
              Your registered email cannot be changed after signup.
            </p>
          </div>
        </section>

        <Form {...form}>
          <form
            className="modern-creator-notifications-form"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <section className="modern-creator-notifications-toggles-section">
              <div className="modern-creator-notifications-toggles-card">
                <h2 className="modern-creator-notifications-toggles-title">
                  Notification Preferences
                </h2>

                <div className="modern-creator-notifications-toggles-list">
                  {toggleConfig.map(
                    ({ key, name, label, description, isMaster }) => (
                      <FormField
                        key={key}
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <FormItem className="modern-creator-notifications-toggle-item">
                            <div className="modern-creator-notifications-toggle-info">
                              <p className="modern-creator-notifications-toggle-name">
                                {label}
                              </p>
                              <p className="modern-creator-notifications-toggle-description">
                                {description}
                              </p>
                            </div>

                            <FormControlWithFocus fieldName={name}>
                              <label className="modern-creator-notifications-toggle-switch">
                                <input
                                  ref={field.ref}
                                  type="checkbox"
                                  className="modern-creator-notifications-toggle-input"
                                  checked={Boolean(field.value)}
                                  onChange={(event) => {
                                    const nextValue = event.target.checked;
                                    handleToggleChange(name, nextValue, isMaster, field);
                                  }}
                                  onBlur={field.onBlur}
                                  disabled={
                                    isSubmitting ||
                                    form.formState.isSubmitting
                                  }
                                />
                                <div className="modern-creator-notifications-toggle-slider"></div>
                              </label>
                            </FormControlWithFocus>
                          </FormItem>
                        )}
                      />
                    )
                  )}
                </div>
              </div>
            </section>

            <section className="modern-creator-notifications-save-section">
              <button
                type="submit"
                className="modern-creator-notifications-save-btn"
                disabled={isSaveDisabled}
              >
                {isSubmitting || form.formState.isSubmitting
                  ? 'Saving...'
                  : 'Save Preferences'}
              </button>
            </section>
          </form>
        </Form>
      </main>
    </div>
  );
};

export default ModernCreatorNotifications;
